import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { batchIdToProductId, isBatchId, byId } from '../../../../subapps/shipments/common/utils'
import { updateCurrentMasterData } from '../../../../van-shared/utils/utils'

// Neither of the master-data reads below suits an online-only user as it
// stands: `product.listAll()` reads the local integrated-data pouch, which such
// a user never replicates, so it answers [] rather than failing — leaving every
// product on the page labelled with its bare id. And `batch.list()` is a full
// allDocs scan straight against remote couch, a db the browser is not supposed
// to talk to on this path at all.
//
// Agave exposes neither an "all products" nor an "all batches" endpoint, and
// this page needs neither: the consumers of these slices resolve exactly the
// products and batches named in the shipments' counts. So resolve those two
// sets by id over agave (`product/by-ids` and `batch/by-ids`, both of which
// already run under the defaultUser middleware for precisely this "caller has
// no user db" case, reading the shared master data server-side).
//
// Mirrors the same id-scoped approach the LMD list uses for locations.
//
// Counts of a batch-tracked shipment are keyed by batch id, everything else by
// plain product id — so the product set comes off every key, the batch set only
// off the ones that actually name a batch.
const masterDataIdsOfShipments = shipments => {
  const productIds = new Set()
  const batchIds = new Set()
  Object.keys(shipments || {}).forEach(snapshotId => {
    const counts = (shipments[snapshotId] || {}).counts || {}
    Object.keys(counts).forEach(countId => {
      const productId = batchIdToProductId(countId)
      // Lowercase is needed for PSM.
      if (productId) productIds.add(productId.toLowerCase())
      if (isBatchId(countId)) batchIds.add(countId)
    })
  })
  return { productIds: [...productIds], batchIds: [...batchIds] }
}

// A page with no shipments needs no products or batches, and either by-ids
// endpoint with an empty list is a round trip that can only answer [] — agave
// in fact rejects the empty list with a 400.
const resolveProductsByIds = (api, productIds) =>
  productIds.length ? api.product.getProductsViaIds(productIds) : Promise.resolve([])

const resolveBatchesByIds = (api, batchIds) =>
  batchIds.length ? api.batch.getBatchesViaIds(batchIds) : Promise.resolve([])

const maybeSubscribeToChanges = (api, dispatch) => {
  if (!maybeSubscribeToChanges.setup) {
    maybeSubscribeToChanges.setup = true
    return api.batch.onListUpdated(batches => dispatch(updateBatches(batches)))
  }
  return api.batch.list()
}

const getProcessedMasterData = (batches, products, manufacturers) => {
  if (batches) {
    batches = byId(batches, '_id')
  }

  if (products) {
    products = {
      byId: byId(products, '_id'),
      allIds: products.map(doc => doc._id)
    }
  }

  if (manufacturers) {
    manufacturers = {
      byId: byId(manufacturers),
      allIds: manufacturers.map(manufacturer => manufacturer.id)
    }
  }

  return { batches, products, manufacturers }
}

const initialState = {
  batches: {},
  products: { byId: {}, allIds: [] },
  manufacturers: { byId: {}, allIds: [] },
  hasReceivedMasterData: false,
  error: null
}

export const fetchMasterData = createAsyncThunk('shipments/fetchMasterData', async (args, thunkAPI) => {
  const { extra: { api }, dispatch, getState } = thunkAPI

  // The resolver is domain-independent today: an online-only user reads
  // everything through agave, everyone else keeps the legacy path.
  const masterDataViaAgave = !!api.dataSource && api.dataSource() === 'agave'

  // The changes feed is a live subscription on the master-data db. An
  // online-only user is not replicating it and the batches below are resolved
  // per shipment id anyway, so there is nothing here to keep in sync.
  if (!maybeSubscribeToChanges.setup && !masterDataViaAgave) {
    maybeSubscribeToChanges(api, dispatch)
  }

  const { productIds, batchIds } = masterDataViaAgave
    ? masterDataIdsOfShipments(getState().shipments)
    : { productIds: [], batchIds: [] }

  try {
    const [batches, products, manufacturers] = await Promise.all([
      masterDataViaAgave
        ? resolveBatchesByIds(api, batchIds)
        : api.batch.list(),
      masterDataViaAgave
        ? resolveProductsByIds(api, productIds)
        : api.product.listAll(),
      // Manufacturers are a hardcoded list in the api, no db read to redirect.
      api.batch.listManufacturers()
    ])

    const processedMasterData = getProcessedMasterData(batches, products, manufacturers)
    return processedMasterData
  } catch (error) {
    return thunkAPI.rejectWithValue(error.message || 'Failed to fetch master data')
  }
})

export const masterDataSlice = createSlice({
  name: 'masterData',
  initialState,
  reducers: {
    updateBatches: (state, action) => {
      const batches = action.payload
      state.batches = byId(batches, '_id')
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMasterData.fulfilled, (state, action) => {
        if (Object.keys(action.payload).length) {
          const { batches, products, manufacturers } = action.payload
          state.batches = batches
          state.products = products
          state.manufacturers = manufacturers
          state.hasReceivedMasterData = true
        }
      })
      .addCase(fetchMasterData.rejected, (state, action) => {
        state.error = action.payload
        state.hasReceivedMasterData = true
      })
  }
})

const { updateBatches } = masterDataSlice.actions

export const selectMasterData = (state) => {
  const newState = updateCurrentMasterData(state, 'masterDataShipments')
  return newState.masterData
}

export const selectProducts = (state) => {
  const newState = updateCurrentMasterData(state, 'masterDataShipments')
  return newState.masterData.products
}

export const selectHasError = (state) => {
  const newState = updateCurrentMasterData(state, 'masterDataShipments')
  return newState.masterData.error
}

export const selectHasReceivedMasterData = (state) => {
  const newState = updateCurrentMasterData(state, 'masterDataShipments')
  return newState.masterData.hasReceivedMasterData
}

export default masterDataSlice.reducer
